import type { DarkRoomReview } from "./schemas";
import type { AssessmentEnvelope } from "./assessment";

export interface ShadowSignalReport {
  assessment?: AssessmentEnvelope;
  header: {
    artistName: string;
    reviewType: string;
    confidence: string;
    date: string;
    disclaimer: string;
  };
  verdict: string;
  scores: Record<string, number>;
  personaCards: Array<{
    persona: string;
    score: number;
    verdict: string;
    critique?: string;
    strengths: string[];
    weaknesses: string[];
    action: string;
    warning: string;
  }>;
  missingInfo: string[];
  nextActions: string[];
  allNextActions: string[];
  suggestedDepth: string;
  seamlineDraft: DarkRoomReview["seamlineDraft"];
  synthaReadiness: string;
}

export function buildShadowSignalReport(review: DarkRoomReview): ShadowSignalReport {
  const gate = review.personaReviews.find((persona) => persona.persona === "The Gate") ?? review.personaReviews[0];

  return {
    assessment: review.assessment,
    header: {
      artistName: review.artistName,
      reviewType: review.reviewType,
      confidence: review.confidence,
      date: review.assessment?.generatedAt.slice(0, 10) ?? "Unknown — legacy report",
      disclaimer: "SHADOW Signal is a readiness report, not legal advice, distribution approval, or VaultSpark Studios endorsement."
    },
    verdict: gate?.verdict ?? "SHADOW needs more evidence before issuing a useful verdict.",
    scores: review.compositeScores,
    personaCards: review.personaReviews.map((persona) => ({
      persona: persona.persona,
      score: persona.score,
      verdict: persona.verdict,
      critique: persona.critique,
      strengths: persona.strengths,
      weaknesses: persona.weaknesses,
      action: persona.action,
      warning: persona.warning
    })),
    missingInfo: review.recommendation.missingInfo,
    nextActions: review.recommendation.topActions.slice(0, 3),
    allNextActions: review.recommendation.topActions,
    suggestedDepth: review.recommendation.suggestedDepth,
    seamlineDraft: review.seamlineDraft,
    synthaReadiness: summarizeSynthaReadiness(review)
  };
}

function summarizeSynthaReadiness(review: DarkRoomReview): string {
  if (review.synthaHandoff.status === "ready_for_review") {
    return "Ready for Syntha review. No automatic publication.";
  }
  if (review.synthaHandoff.status === "draft") {
    return "Draft handoff can be prepared, but publication remains manual and moderated.";
  }
  return `Not ready for Syntha: ${review.synthaHandoff.validationErrors.join(", ")}`;
}
